import { Permission } from '@/api';
import TableCore from '@/components/table/table-core';
import {
  NUMERIC_FILTER_COLUMNS,
  PERCENTAGE_COLUMNS,
  computeBatchUpdate,
  computeRrp,
  exportToExcel,
  filterNonDistributorPricelistRows,
  getFontColor,
  hasCurrencyDecimalsForAll,
  hasPriceChange,
  isCellChanged,
  isCellEditable as isCellEditableShared,
} from '@/components/table/table-helpers';
import {
  type BaseRowDataForDisplay,
  type EditableField,
  LockableField,
} from '@/components/table/table-types';
import { useFlashCell } from '@/hooks/use-flash-cell';
import { useHasPermissions } from '@/hooks/use-has-permissions';
import { useMissingDecimalsNotification } from '@/hooks/use-missing-decimals-notification';
import { useNotification } from '@/hooks/use-notification';
import { useSimulationErrorNotification } from '@/hooks/use-simulation-error-notification';
import { useUserRole } from '@/hooks/use-user-role';
import type { DownloadOption } from '@/pages/price-planning/create-price-page/types/create-price-table-types';
import { connectColors, themeColors } from '@/theme/colors';
import { LP_MIN_THRESHOLD } from '@/utils/lp-validation';
import { Text } from '@lego/klik-ui';
import { useQueryClient } from '@tanstack/react-query';
import type {
  MRT_Cell,
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
  MRT_Updater,
} from 'material-react-table';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSavePrices } from '../../simulation-queries';
import { HEIGHT_OFFSET, SimulationView } from '../simulation-view-constants';
import {
  getColumnData,
  getColumnGroups,
} from '../utils/simulation-column-config';
import {
  buildOriginalBaselineMap,
  calculatePricingField,
  formatDataForDisplay,
  isRowChangedInPreview,
  recalculateFields,
} from '../utils/simulation-helpers';
import type {
  PricelistSettings,
  RowData,
  RowDataForDisplay,
  SIM_MODAL,
  TableColumn,
} from '../utils/simulation-types';
import SimulationTableToolbar from './toolbar/simulation-table-toolbar';
import type { UploadedPriceRow } from './upload/helpers';

type SimulationTableProps = {
  initialData: Map<string, RowData>;
  simulationData: Map<string, RowData>;
  previewBaseData?: Map<string, RowData>;
  pricelistSettings: Map<string, PricelistSettings>;
  lockedColumn: LockableField | undefined;
  totalLlRrpIndex: Map<string, number>;
  planningYear: number;
  simulationId: number;
  groupedBy?: 'rm' | 'rrp';
  selectedView: SimulationView;
  isReadOnly: boolean;
  downloadTriggerRef: RefObject<HTMLLIElement | null>;
  publishTriggerRef: RefObject<HTMLButtonElement | null>;
  indexTableNode: HTMLDivElement | null;
  setSelectedView: (view: SimulationView) => void;
  setLockedColumn: (column: LockableField | undefined) => void;
  onDataChange: (data: Map<string, RowData>) => void;
  setShowModal: (modal: SIM_MODAL | null) => void;
  isPublishDisabled: boolean;
  onBeforeDownload?: (exportFn: (type: DownloadOption) => void) => void;
  onUploadReady?: (applyFn: (rows: UploadedPriceRow[]) => void) => void;
  lpErrorCells: Set<string>;
  onClearLpErrorCell: (primaryKey: string) => void;
  preference: 'simple' | 'lel';
  setPreference: (preference: 'simple' | 'lel') => void;
  columnFilters: MRT_ColumnFiltersState;
  setColumnFilters: (updater: MRT_Updater<MRT_ColumnFiltersState>) => void;
};

const SimulationTable = ({
  initialData,
  simulationData,
  previewBaseData,
  pricelistSettings,
  lockedColumn,
  totalLlRrpIndex,
  planningYear,
  simulationId,
  groupedBy,
  selectedView,
  isReadOnly: isReadOnlyProp,
  downloadTriggerRef,
  publishTriggerRef,
  indexTableNode,
  setSelectedView,
  setLockedColumn,
  onDataChange,
  setShowModal,
  isPublishDisabled,
  onBeforeDownload,
  onUploadReady,
  lpErrorCells,
  onClearLpErrorCell,
  preference,
  setPreference,
  columnFilters,
  setColumnFilters,
}: SimulationTableProps) => {
  const queryClient = useQueryClient();
  const showNotification = useNotification();
  const showMissingDecimalsError = useMissingDecimalsNotification();
  const showSimulationError = useSimulationErrorNotification();
  const canEditPrices = useHasPermissions([Permission.EditSimulationPrices]);
  const isReadOnly = isReadOnlyProp || !canEditPrices;
  const { isGlobalPriceManager } = useUserRole();

  const isLel = preference === 'lel';

  // Session-proof baseline: price fields use immutable asIs* (creation-time)
  // values so revert and change detection survive browser refresh.
  // Piped through recalculateFields to keep derived fields (lpDelta, indexes,
  // listPriceFmcFactor, nip) consistent with the overridden lp/rrp/rm.
  const originalBaseline = useMemo(
    () =>
      recalculateFields(
        buildOriginalBaselineMap(initialData),
        pricelistSettings,
      ),
    [initialData, pricelistSettings],
  );

  const formattedInitialData = useMemo(
    () =>
      formatDataForDisplay(
        Array.from(originalBaseline.values()),
        pricelistSettings,
        totalLlRrpIndex,
      ),
    [originalBaseline, pricelistSettings, totalLlRrpIndex],
  );
  // recalculate fields when pricelistSettings changes or LP/RRP is edited
  const dataWithRecalculatedFields = useMemo(
    () => recalculateFields(simulationData, pricelistSettings),
    [simulationData, pricelistSettings],
  );

  const formattedSimulationData = useMemo(
    () =>
      formatDataForDisplay(
        Array.from(dataWithRecalculatedFields.values()),
        pricelistSettings,
        totalLlRrpIndex,
      ).map((row) => ({
        ...row,
        hasPriceChange: hasPriceChange(row, formattedInitialData) ? '1' : '0',
      })),
    [
      dataWithRecalculatedFields,
      formattedInitialData,
      pricelistSettings,
      totalLlRrpIndex,
    ],
  );

  useEffect(() => {
    onDataChange?.(dataWithRecalculatedFields);
  }, [dataWithRecalculatedFields, onDataChange]);

  const columnGroups = useMemo(() => getColumnGroups(groupedBy), [groupedBy]);

  const columnData = useMemo(
    () => getColumnData(lockedColumn, isLel, groupedBy, isGlobalPriceManager),
    [lockedColumn, isLel, groupedBy, isGlobalPriceManager],
  );

  const [rowSelection, setRowSelection] = useState<{
    state: MRT_RowSelectionState;
    items: RowData[];
  }>({ state: {}, items: [] });
  const [shouldBlockPageLeave, setShouldBlockPageLeave] = useState(false);

  const { flashingCells, flashCell: flashLpCell } = useFlashCell();

  const { mutate: saveEdited } = useSavePrices(
    simulationId,
    () => {
      setShouldBlockPageLeave(false);
      queryClient.invalidateQueries({
        queryKey: ['simulationData', simulationId],
      });
    },
    (error) => {
      setShouldBlockPageLeave(true);
      showSimulationError(error, 'save prices');
      queryClient.invalidateQueries({
        queryKey: ['simulationData', simulationId],
      });
    },
  );

  useEffect(() => {
    if (!shouldBlockPageLeave) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // some browsers require setting returnValue to show the native leave-page confirmation dialog.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlockPageLeave]);

  const batchUpdatePricingField = useCallback(
    (field: EditableField, value: number, isChangeProportional = false) => {
      const nonDistributorRows = filterNonDistributorPricelistRows(
        rowSelection.items,
        field,
      );
      if (nonDistributorRows.length === 0) {
        showNotification({
          type: 'warning',
          title: 'No eligible rows',
          description: 'The selected rows do not support editing RRP or RM.',
        });
        return;
      }
      if (
        !hasCurrencyDecimalsForAll(nonDistributorRows, showMissingDecimalsError)
      )
        return;
      const newData = computeBatchUpdate(
        dataWithRecalculatedFields,
        nonDistributorRows,
        field,
        value,
        isChangeProportional,
        lockedColumn,
        initialData,
        (f, row, val, locked) =>
          calculatePricingField(f, row, val, locked, pricelistSettings),
      );

      const violatingRows = nonDistributorRows.filter((row) => {
        const updatedRow = newData.get(row.primaryKey);
        if (!updatedRow || updatedRow.lp >= LP_MIN_THRESHOLD) return false;
        const baseline = initialData.get(row.primaryKey);
        return baseline !== undefined && baseline.asIsLp > 0;
      });

      if (violatingRows.length > 0) {
        for (const row of violatingRows) {
          const baseline = initialData.get(row.primaryKey);
          const current = newData.get(row.primaryKey);
          if (baseline && current) {
            // Guard uses asIsLp (immutable original price) to decide *whether* to
            // revert. Revert target prefers last-saved lp to preserve intermediate
            // edits, falling back to asIsLp when lp is also zero.
            const revertLp = baseline.lp > 0 ? baseline.lp : baseline.asIsLp;
            const reverted = calculatePricingField(
              LockableField.lp,
              current,
              revertLp,
              lockedColumn,
              pricelistSettings,
            );
            newData.set(row.primaryKey, reverted);
          }
        }
        showNotification({
          type: 'warning',
          title: 'Notice',
          description: `${violatingRows.length} row(s) were kept at their previous List Price because it must be greater than 0.`,
        });
      }

      onDataChange(newData);

      const priceRows = nonDistributorRows
        .map((row) => {
          const currentRow = dataWithRecalculatedFields.get(row.primaryKey);
          const updatedRow = newData.get(row.primaryKey);
          return {
            priceUnitId: row.priceUnitId,
            productId: row.productId,
            ...(currentRow?.rrp !== updatedRow?.rrp &&
            updatedRow?.rrp !== undefined
              ? { rrp: updatedRow.rrp }
              : {}),
            ...(currentRow?.rm !== updatedRow?.rm &&
            updatedRow?.rm !== undefined
              ? { rm: updatedRow.rm }
              : {}),
            ...(currentRow?.lp !== updatedRow?.lp &&
            updatedRow?.lp !== undefined
              ? { lp: updatedRow.lp }
              : {}),
            ...(currentRow?.vat !== updatedRow?.vat &&
            updatedRow?.vat !== undefined
              ? { vat: updatedRow.vat }
              : {}),
          };
        })
        .filter(
          (row) =>
            row.rrp !== undefined ||
            row.rm !== undefined ||
            row.lp !== undefined ||
            row.vat !== undefined,
        );

      if (priceRows.length > 0) {
        //if the save call is successful, it will clear the shouldBlockPageLeave flag
        setShouldBlockPageLeave(true);
        saveEdited({ priceRows });
      }
    },
    [
      showMissingDecimalsError,
      initialData,
      lockedColumn,
      pricelistSettings,
      dataWithRecalculatedFields,
      rowSelection,
      onDataChange,
      saveEdited,
      showNotification,
    ],
  );

  const isCellEditable = useCallback(
    (columnKey: TableColumn, row: RowDataForDisplay) =>
      !isReadOnly &&
      isCellEditableShared(columnKey, row, lockedColumn, columnData),
    [columnData, lockedColumn, isReadOnly],
  );
  const isRowChangedInPreviewRow = useCallback(
    (row: RowDataForDisplay): boolean =>
      isRowChangedInPreview(row, previewBaseData, dataWithRecalculatedFields),
    [previewBaseData, dataWithRecalculatedFields],
  );
  /** Returns the text color for a standard cell, based on preview/initial change state. */
  const getCellColor = useCallback(
    (row: RowDataForDisplay, key: TableColumn): string => {
      if (isRowChangedInPreviewRow(row))
        return themeColors.highlight.pink.default;

      const isChangedFromInitial =
        !previewBaseData &&
        isCellChanged(
          row,
          key as keyof BaseRowDataForDisplay,
          formattedInitialData,
        );
      if (isChangedFromInitial) return themeColors.support.warning.default;

      const columnType = isCellEditable(key, row)
        ? columnData[key]?.columnType
        : undefined;
      return getFontColor(columnType);
    },
    [
      previewBaseData,
      formattedInitialData,
      isCellEditable,
      columnData,
      isRowChangedInPreviewRow,
    ],
  );

  /** Returns the text color for a ppcMark cell, adding ABOVE/BELOW overrides on top of getCellColor. */
  const getPpcMarkColor = useCallback(
    (mark: string, row: RowDataForDisplay, key: TableColumn): string =>
      mark === 'ABOVE'
        ? themeColors.content.subtle
        : mark === 'BELOW'
          ? connectColors.core.purple[1100]
          : getCellColor(row, key),
    [getCellColor],
  );

  // Clear row selection when the table becomes read-only
  useEffect(() => {
    if (isReadOnly) {
      setRowSelection({ state: {}, items: [] });
    }
  }, [isReadOnly]);

  const getCellComponent = (
    cell: MRT_Cell<RowDataForDisplay>,
    key: TableColumn,
  ) => {
    const getCellValue = () => cell.row.original[key];

    if (key === 'ppcMark') {
      const mark = cell.row.original.ppcMark;
      if (!mark) return null;
      return (
        <Text
          isTruncated
          fontSize={'10px'}
          fontWeight={700}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
          color={getPpcMarkColor(mark, cell.row.original, key)}
        >
          {mark}
        </Text>
      );
    }

    return (
      <Text
        className="body-xs-regular"
        isTruncated
        color={getCellColor(cell.row.original, key as TableColumn)}
      >
        {getCellValue()}
      </Text>
    );
  };

  const saveEditedData = useCallback(
    (updatedRow: RowData) => {
      const originalRow = simulationData.get(updatedRow.primaryKey);

      if (updatedRow.lp < LP_MIN_THRESHOLD) {
        const baselineRow = initialData.get(updatedRow.primaryKey);
        const hasExistingPrice = baselineRow && baselineRow.asIsLp > 0;

        if (hasExistingPrice) {
          const revertLp =
            originalRow && originalRow.lp > 0
              ? originalRow.lp
              : baselineRow.lp > 0
                ? baselineRow.lp
                : baselineRow.asIsLp;
          flashLpCell(updatedRow.primaryKey);
          showNotification({
            type: 'warning',
            title: 'Notice',
            description:
              'The List Price was adjusted back to its previous value because it must be greater than 0.',
          });
          const reverted = calculatePricingField(
            LockableField.lp,
            updatedRow,
            revertLp,
            lockedColumn,
            pricelistSettings,
          );
          onDataChange(
            new Map(simulationData).set(updatedRow.primaryKey, reverted),
          );
          onClearLpErrorCell(updatedRow.primaryKey);
          return;
        }
      }

      onClearLpErrorCell(updatedRow.primaryKey);

      const hasChanges =
        originalRow?.rrp !== updatedRow.rrp ||
        originalRow?.rm !== updatedRow.rm ||
        originalRow?.lp !== updatedRow.lp ||
        originalRow?.vat !== updatedRow.vat ||
        originalRow?.comment !== updatedRow.comment;

      if (!hasChanges) {
        return;
      }

      setShouldBlockPageLeave(true);
      saveEdited({
        priceRows: [
          {
            priceUnitId: updatedRow.priceUnitId,
            productId: updatedRow.productId,
            ...(originalRow?.rrp !== updatedRow.rrp && {
              rrp: updatedRow.rrp,
            }),
            ...(originalRow?.rm !== updatedRow.rm && { rm: updatedRow.rm }),
            ...(originalRow?.lp !== updatedRow.lp && {
              lp: updatedRow.lp,
            }),
            ...(originalRow?.vat !== updatedRow.vat && { vat: updatedRow.vat }),
            ...(originalRow?.comment !== updatedRow.comment && {
              comment: updatedRow.comment,
            }),
          },
        ],
      });
    },
    [
      saveEdited,
      simulationData,
      initialData,
      lockedColumn,
      pricelistSettings,
      flashLpCell,
      showNotification,
      onDataChange,
      onClearLpErrorCell,
    ],
  );

  const saveMassRevert = useCallback(
    (revertedRows: RowData[]) => {
      const priceRows = revertedRows
        .map((revertedRow) => {
          onClearLpErrorCell(revertedRow.primaryKey);
          const currentRow = simulationData.get(revertedRow.primaryKey);
          return {
            priceUnitId: revertedRow.priceUnitId,
            productId: revertedRow.productId,
            ...(currentRow?.rrp !== revertedRow.rrp && {
              rrp: revertedRow.rrp,
            }),
            ...(currentRow?.rm !== revertedRow.rm && { rm: revertedRow.rm }),
            ...(currentRow?.lp !== revertedRow.lp && {
              lp: revertedRow.lp,
            }),
            ...(currentRow?.vat !== revertedRow.vat && {
              vat: revertedRow.vat,
            }),
          };
        })
        .filter(
          (row) =>
            row.rrp !== undefined ||
            row.rm !== undefined ||
            row.lp !== undefined ||
            row.vat !== undefined,
        );

      if (priceRows.length === 0) return;

      setShouldBlockPageLeave(true);
      saveEdited({ priceRows });
    },
    [saveEdited, simulationData, onClearLpErrorCell],
  );

  const applyUploadedPriceRows = useCallback(
    async (uploadedRows: UploadedPriceRow[]) => {
      const rowByProductAndPriceUnit = new Map<string, RowData>();
      for (const row of dataWithRecalculatedFields.values()) {
        rowByProductAndPriceUnit.set(
          `${row.productId}::${row.priceUnitId}`,
          row,
        );
      }

      type MatchedUpload = {
        uploadedRow: UploadedPriceRow;
        matchedRow: RowData;
        uploadedRrp: number | null;
      };

      const matchedUploads: MatchedUpload[] = [];

      let matchedCount = 0;
      for (const uploadedRow of uploadedRows) {
        const matchedRow = rowByProductAndPriceUnit.get(
          `${uploadedRow.productId}::${uploadedRow.priceUnitId}`,
        );

        if (!matchedRow) {
          continue;
        }

        matchedCount += 1;

        let uploadedRrp = uploadedRow.rrp;

        if (
          uploadedRrp === null &&
          uploadedRow.rm !== null &&
          uploadedRow.lp !== null &&
          lockedColumn !== 'lp' &&
          lockedColumn !== 'rrp'
        ) {
          uploadedRrp = computeRrp(
            uploadedRow.lp,
            uploadedRow.rm,
            matchedRow.vat,
          );
        }

        matchedUploads.push({
          uploadedRow,
          matchedRow,
          uploadedRrp,
        });
      }

      if (
        !hasCurrencyDecimalsForAll(
          matchedUploads.map((m) => m.matchedRow),
          showMissingDecimalsError,
        )
      )
        return;

      const updatedData = new Map(dataWithRecalculatedFields);
      const changedPriceRows: Array<{
        productId: number;
        priceUnitId: number;
        rrp?: number;
        rm?: number;
        lp?: number;
        vat?: number;
        comment?: string;
      }> = [];

      for (const matchedUpload of matchedUploads) {
        const { uploadedRow, matchedRow } = matchedUpload;

        const uploadedRrp = matchedUpload.uploadedRrp;

        const shouldIgnoreUploadedLp =
          uploadedRrp !== null && uploadedRow.rm !== null;

        const effectiveUploadedValues = {
          rrp: lockedColumn === 'rrp' ? null : uploadedRrp,
          rm: lockedColumn === 'rm' ? null : uploadedRow.rm,
          lp:
            lockedColumn === 'lp' || shouldIgnoreUploadedLp
              ? null
              : uploadedRow.lp,
        };

        let recalculatedRow = matchedRow;
        if (effectiveUploadedValues.rrp !== null) {
          recalculatedRow = calculatePricingField(
            LockableField.rrp,
            recalculatedRow,
            effectiveUploadedValues.rrp,
            lockedColumn,
            pricelistSettings,
          );
        }

        if (effectiveUploadedValues.rm !== null) {
          recalculatedRow = calculatePricingField(
            LockableField.rm,
            recalculatedRow,
            effectiveUploadedValues.rm,
            lockedColumn,
            pricelistSettings,
          );
        }

        if (effectiveUploadedValues.lp !== null) {
          recalculatedRow = calculatePricingField(
            LockableField.lp,
            recalculatedRow,
            effectiveUploadedValues.lp,
            lockedColumn,
            pricelistSettings,
          );
        }

        const newComment = uploadedRow.comment;
        const commentChanged =
          newComment !== null && matchedRow.comment !== newComment;

        const priceChanged =
          matchedRow.rrp !== recalculatedRow.rrp ||
          matchedRow.rm !== recalculatedRow.rm ||
          matchedRow.lp !== recalculatedRow.lp ||
          matchedRow.vat !== recalculatedRow.vat;

        if (!priceChanged && !commentChanged) {
          continue;
        }

        if (priceChanged && recalculatedRow.lp < LP_MIN_THRESHOLD) {
          const baselineAsIsLp = initialData.get(
            recalculatedRow.primaryKey,
          )?.asIsLp;
          if (baselineAsIsLp !== undefined && baselineAsIsLp > 0) {
            continue;
          }
        }

        if (commentChanged) {
          recalculatedRow = { ...recalculatedRow, comment: newComment };
        }

        updatedData.set(recalculatedRow.primaryKey, recalculatedRow);
        const changedPriceRow = {
          productId: recalculatedRow.productId,
          priceUnitId: recalculatedRow.priceUnitId,
          ...(matchedRow.rrp !== recalculatedRow.rrp && {
            rrp: recalculatedRow.rrp,
          }),
          ...(matchedRow.rm !== recalculatedRow.rm && {
            rm: recalculatedRow.rm,
          }),
          ...(matchedRow.lp !== recalculatedRow.lp && {
            lp: recalculatedRow.lp,
          }),
          ...(matchedRow.vat !== recalculatedRow.vat && {
            vat: recalculatedRow.vat,
          }),
          ...(commentChanged && {
            comment: newComment,
          }),
        };

        changedPriceRows.push(changedPriceRow);
      }

      const unmatchedCount = uploadedRows.length - matchedCount;
      const unchangedCount = matchedCount - changedPriceRows.length;

      if (matchedCount === 0) {
        showNotification({
          type: 'warning',
          title: 'No matching products found',
          description:
            'No simulation rows matched the uploaded product id and price unit id pairs.',
        });
        return;
      }

      if (changedPriceRows.length === 0) {
        showNotification({
          type: 'warning',
          title: 'No price updates applied',
          description:
            'Matching rows were found, but uploaded values were the same as current values.',
        });
        return;
      }

      onDataChange(updatedData);
      setShouldBlockPageLeave(true);
      saveEdited({ priceRows: changedPriceRows });

      const summaryParts: string[] = [
        `${changedPriceRows.length} row(s) updated`,
      ];
      if (unchangedCount > 0) {
        summaryParts.push(`${unchangedCount} unchanged (skipped)`);
      }
      if (unmatchedCount > 0) {
        summaryParts.push(`${unmatchedCount} unmatched (product not found)`);
      }
      if (lockedColumn) {
        summaryParts.push(
          `${lockedColumn.toUpperCase()} column locked — uploaded values ignored`,
        );
      }

      showNotification({
        type: 'success',
        title: 'Upload applied',
        description: summaryParts.join(' • '),
      });
    },
    [
      showMissingDecimalsError,
      dataWithRecalculatedFields,
      initialData,
      lockedColumn,
      onDataChange,
      pricelistSettings,
      saveEdited,
      showNotification,
    ],
  );

  useEffect(() => {
    onUploadReady?.(applyUploadedPriceRows);
  }, [onUploadReady, applyUploadedPriceRows]);

  const [indexTableHeight, setIndexTableHeight] = useState(0);

  useEffect(() => {
    if (!indexTableNode) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIndexTableHeight(entry.contentRect.height);
      }
    });
    resizeObserver.observe(indexTableNode);
    return () => resizeObserver.disconnect();
  }, [indexTableNode]);

  const tableHeight =
    selectedView === SimulationView.BASIC
      ? `calc(100vh - ${indexTableHeight + HEIGHT_OFFSET}px)`
      : `calc(100vh - ${HEIGHT_OFFSET}px)`;

  return (
    <>
      <TableCore<RowData, RowDataForDisplay, TableColumn>
        formattedData={formattedSimulationData}
        columnData={columnData}
        columnGroups={columnGroups}
        isCellEditable={isCellEditable}
        cellComponent={getCellComponent}
        initialData={initialData}
        revertBaseline={originalBaseline}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        planningYear={planningYear}
        formattedInitialData={
          previewBaseData ? formattedSimulationData : formattedInitialData
        }
        calculatePricingField={(field, row, value, locked) =>
          calculatePricingField(field, row, value, locked, pricelistSettings)
        }
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        data={dataWithRecalculatedFields}
        setData={onDataChange}
        lockedColumn={lockedColumn}
        tableHeight={tableHeight}
        onEdit={saveEditedData}
        onMassRevert={saveMassRevert}
        isReadOnly={isReadOnly}
        getToolbar={(table) => (
          <SimulationTableToolbar
            table={table}
            lockedColumn={lockedColumn}
            setLockedColumn={setLockedColumn}
            isLel={isLel}
            canUploadFile={isGlobalPriceManager && !isReadOnly}
            setPreference={setPreference}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            setShowModal={setShowModal}
            isPublishDisabled={isPublishDisabled}
            simulationId={simulationId}
            onDownloadModalOpen={() => {
              const currentColumnVisibility = table.getState().columnVisibility;
              const currentFilteredRows = table
                .getFilteredRowModel()
                .rows.map((row) => row.original);
              onBeforeDownload?.((downloadType) => {
                exportToExcel(
                  downloadType,
                  columnData,
                  formattedSimulationData,
                  currentFilteredRows,
                  currentColumnVisibility,
                  'Simulation',
                  'simulation.xlsx',
                );
              });
            }}
            downloadTriggerRef={downloadTriggerRef}
            publishTriggerRef={publishTriggerRef}
          />
        )}
        groupedBy={groupedBy}
        overrideCellBackgroundColor={(row, columnKey) => {
          if (
            columnKey === 'lp' &&
            (flashingCells.has(row.primaryKey) ||
              lpErrorCells.has(row.primaryKey))
          ) {
            return connectColors.core.red[200];
          }
          return isRowChangedInPreviewRow(row)
            ? themeColors.highlight.pink.inverse
            : undefined;
        }}
        customCellBackgroundColor={(_row, columnKey) => {
          if (
            columnKey === 'rrp' ||
            columnKey === 'rm' ||
            columnKey === 'lp' ||
            columnKey === 'comment'
          )
            return themeColors.interactive.primary.subtle.selected;
          return undefined;
        }}
        batchUpdatePricingField={batchUpdatePricingField}
        numericFilterColumns={NUMERIC_FILTER_COLUMNS}
        percentageColumns={PERCENTAGE_COLUMNS}
        allowEditingVat={isGlobalPriceManager}
        pinnedColumns={[
          'mrt-row-select',
          'price-list', //header group
          'priceUnitId',
          'product-data-pinned', //header group
          'productId',
          'productName',
        ]}
      />
    </>
  );
};

export default SimulationTable;
